import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.dataProviderApiEnv;
const baseUrl = API.address;

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) return res.json();
    throw new Error(`API error: ${res.status}`);
};

export const isTimeoutOrNetworkError = (error: unknown): boolean => {
    if (!(error instanceof Error)) return false;
    if ((error as Error & { isNetworkError?: boolean }).isNetworkError === true) return true;
    return /^API error: (408|504)$/.test(error.message);
};

const request = async (url: string, init: RequestInit) => {
    try {
        return await fetch(url, init);
    } catch {
        const networkError = new Error('Network error') as Error & { isNetworkError?: boolean };
        networkError.isNetworkError = true;
        throw networkError;
    }
};

const get = async (path: string) => handleResponse(await request(`${baseUrl}${path}`, { headers: await getHeaders() }));
const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await request(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const qs = (params: Record<string, unknown>) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

const downloadJson = async (path: string, _filename: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${baseUrl}${path}`, { headers });
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);
    const json = await res.json();
    if (json.downloadUrl) { window.open(json.downloadUrl, '_blank'); return; }
    throw new Error('Download URL not found');
};

const downloadCsv = async (path: string, filename: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${baseUrl}${path}`, { headers });
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        const json = await res.json();
        if (json.downloadUrl) { window.open(json.downloadUrl, '_blank'); return; }
        throw new Error('Unexpected JSON response');
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = filename;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};

export const dataProviderApi = {
    // Batch Reports
    listBatchReports: (params?: { provider?: string; reportType?: string; status?: string; identification?: string; page?: number; pageSize?: number }) =>
        get(`/batch-reports/page-list${qs(params || {})}`),

    getBatchReport: (id: string) => get(`/batch-reports/${id}`),

    getBatchReportPageView: (id: string, params?: { page?: number; pageSize?: number; order?: string }) =>
        get(`/batch-reports/${id}/page-view${qs(params || {})}`),

    getBatchReportForm: () => get(`/batch-reports/page-form`),

    createBatchReport: (data: Record<string, unknown>) => post(`/batch-reports`, data),

    refreshBatchStatus: (id: string) => post(`/batch-reports/${id}/refresh-status`, {}),

    interruptBatch: (id: string) => post(`/batch-reports/${id}/interrupt`, {}),

    reprocessReports: (batchId: string, documents: string[]) =>
        post(`/process`, { batchReportId: batchId, documents }),

    // Reports
    getReports: (batchId: string, params?: { page?: number; pageSize?: number; document?: string; status?: string }) =>
        get(`/batch-reports/${batchId}/reports${qs(params || {})}`),

    getReportDetail: (batchId: string, document: string) =>
        get(`/reports/${batchId}/${encodeURIComponent(document)}`),

    getReportPageView: (batchId: string, document: string) =>
        get(`/reports/${batchId}/${encodeURIComponent(document)}/page-view`),

    getPayloadContent: (batchId: string, document: string, s3Key: string) =>
        get(`/reports/${batchId}/${encodeURIComponent(document)}/payload-content${qs({ s3Key })}`),

    // Downloads
    downloadReportPayload: (batchId: string, document: string) =>
        downloadJson(`/reports/${batchId}/${encodeURIComponent(document)}/payload`, `payload-${document}.json`),

    downloadReportExtraction: (batchId: string, document: string) =>
        downloadJson(`/reports/${batchId}/${encodeURIComponent(document)}/extraction`, `extraction-${document}.json`),

    downloadBatchCsv: (batchId: string) =>
        downloadCsv(`/batch-reports/${batchId}/download`, `batch-report-${batchId}.csv`),

    // Report Types
    listReportTypes: () => get(`/report-types/page-list`),
    getReportType: (id: string) => get(`/report-types/${id}/page-view`),
};
